// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddJournalEntryModal from 'main-app/components/AddJournalEntryModal';
import JournalEntryDetailModal from 'main-app/components/JournalEntryDetailModal';
import FilterControls, {
  type FilterValue,
} from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedJournalEntriesContainer from './PaginatedJournalEntriesContainer';

type Props = { history: RouterHistory };

type State = {
  addModalOpen: boolean,
  detailEntry: ?Object,
  search: string,
  graphqlSearchValue: string,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class JournalEntries extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      addModalOpen: false,
      detailEntry: null,
      search: '',
      graphqlSearchValue: '',
    };
    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;
    if (!search) return DEFAULT_FILTER;
    return {
      ...DEFAULT_FILTER,
      ...qs.parse(search, { ignoreQueryPrefix: true }),
    };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;
    history.replace(
      '/journal-entries?' +
        qs.stringify(filters, { encode: true, arrayFormat: 'brackets' }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;
    return (
      !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) ||
      graphqlSearchValue
    );
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;
    const queryVariables: Object = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: { field: filters.sort, direction: filters.direction },
    };

    if (queryVariables.entryDate) {
      const [start, end] = queryVariables.entryDate.split(' - ');
      queryVariables.startDate = start;
      queryVariables.endDate = end;
      delete queryVariables.entryDate;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;
    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState({ search }, this.handleDebouncedSearch);
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;
    this.setState({ graphqlSearchValue: search });
    Analytics.trackEvent('Search Applied', {
      listName: 'Journal Entries',
      searchString: search,
    });
  };

  handleOpenCreateModal = () => {
    this.setState({ addModalOpen: true });
  };

  handleCloseCreateModal = () => {
    this.setState({ addModalOpen: false });
  };

  handleViewDetail = (entry: Object) => {
    this.setState({ detailEntry: entry });
  };

  handleCloseDetail = () => {
    this.setState({ detailEntry: null });
  };

  render() {
    const { history } = this.props;
    const { search, addModalOpen, detailEntry } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query JournalEntriesQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: JournalEntrySort
            $statuses: [JournalEntryStatus!]
            $sourceTypes: [JournalSourceType!]
            $startDate: Date
            $endDate: Date
          ) {
            ...PaginatedJournalEntriesContainer_journalEntries
              @arguments(
                search: $search
                sortBy: $sortBy
                statuses: $statuses
                sourceTypes: $sourceTypes
                startDate: $startDate
                endDate: $endDate
              )
            viewer {
              id
              permissions
            }
          }
        `}
        variables={{ ...this.compileQueryVariables(), count: 30, cursor: null }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Journal Entries')}
              createButtonLabel={props && i18n.t('New Entry')}
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t(
                'Search by entry number or memo',
              )}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedJournalEntriesContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                onViewDetail={this.handleViewDetail}
                journalEntries={props}
              />
            )}
            {addModalOpen && (
              <AddJournalEntryModal onClose={this.handleCloseCreateModal} />
            )}
            {detailEntry && (
              <JournalEntryDetailModal
                entry={detailEntry}
                onClose={this.handleCloseDetail}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default JournalEntries;
